import axios from "axios";
import { createAsyncThunk } from "@reduxjs/toolkit";

import { apiClient } from "@/config/api";
import { updateUser } from "@/store/slices/authSlice";

import {
  AnswerQuestionnaireBody,
  AnswerQuestionnaireByUserBody,
  CreateDocumentBody,
  CreateDocumentForUserBody,
  CreateFolderBody,
  CreateFolderForUserBody,
  DeleteDocumentBody,
  GetDocumentsBody,
  GetDocumentsByFolderBody,
  GetDocumentsByFolderForUserBody,
  GetMoreFilesByFolderBody,
  GetMoreFilesByFolderForUserBody,
  GetMoreFilesForUserBody,
  GetMoreFoldersForUserBody,
  GetMoreNotifications,
  MarkNotificationRead,
  UpdateEmergencyContactBody,
  UpdateInfoBody,
  UpdateProfilePictureBody,
} from "./types";

export const updateProfilePicture = createAsyncThunk(
  "profile/profile-picture",
  async (body: UpdateProfilePictureBody, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("profilePicture", body.profilePicture);

      const response = await apiClient.put(
        "/profile/profile-picture",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Update profile picture failed"
        );
      }
      return rejectWithValue("Update profile picture failed");
    }
  }
);

export const updateInfo = createAsyncThunk(
  "profile/info",
  async (body: UpdateInfoBody, { rejectWithValue }) => {
    try {
      if (!body.secondaryEmail) {
        body.secondaryEmail = null;
      }
      const response = await apiClient.put("/profile/info", body);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Update info failed"
        );
      }
      return rejectWithValue("Update info failed");
    }
  }
);

export const updateEmergencyContact = createAsyncThunk(
  "profile/emergency-contact",
  async (body: UpdateEmergencyContactBody, { dispatch, rejectWithValue }) => {
    try {
      if (!body.emergencyContactName) {
        body.emergencyContactName = null;
      }
      if (!body.emergencyContactPhoneNumber) {
        body.emergencyContactPhoneNumber = null;
      }
      const response = await apiClient.put("/profile/emergency-contact", body);

      dispatch(
        updateUser({
          emergencyContactName: body.emergencyContactName,
          emergencyContactPhoneNumber: body.emergencyContactPhoneNumber,
        })
      );

      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Update emergency contact failed"
        );
      }
      return rejectWithValue("Update emergency contact failed");
    }
  }
);

export const removeProfilePicture = createAsyncThunk(
  "profile/profile-picture-delete",
  async (_: undefined, { dispatch, rejectWithValue }) => {
    try {
      const response = await apiClient.delete("/profile/profile-picture");

      dispatch(updateUser({ profileImage: null }));

      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Remove profile picture failed"
        );
      }
      return rejectWithValue("Remove profile picture failed");
    }
  }
);

export const deactivateAccount = createAsyncThunk(
  "user/request-deactivation",
  async (_: undefined, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("user/request-deactivation");
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Request deactivation failed"
        );
      }
      return rejectWithValue("Request deactivation failed");
    }
  }
);

export const getQuestionnaire = createAsyncThunk(
  "questionnaire",
  async (_: undefined, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("questionnaire");
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Get questionnaire failed"
        );
      }
      return rejectWithValue("Get questionnaire failed");
    }
  }
);

export const getQuestionnaireAnswered = createAsyncThunk(
  "questionnaire/answered",
  async (_: undefined, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("questionnaire/answered");
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Get questionnaire failed"
        );
      }
      return rejectWithValue("Get questionnaire failed");
    }
  }
);

export const answerQuestionnaire = createAsyncThunk(
  "questionnaire/answer",
  async (body: AnswerQuestionnaireBody, { rejectWithValue }) => {
    try {
      const response = await apiClient.post("questionnaire/answer", body);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Answer questionnaire failed"
        );
      }
      return rejectWithValue("Answer questionnaire failed");
    }
  }
);

export const getQuestionnaireAnsweredByUser = createAsyncThunk(
  "questionnaire/answered/user",
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(
        `questionnaire/user/${userId}/answered`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Get questionnaire failed"
        );
      }
      return rejectWithValue("Get questionnaire failed");
    }
  }
);

export const answerQuestionnaireByUser = createAsyncThunk(
  "questionnaire/answer/user",
  async (body: AnswerQuestionnaireByUserBody, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(
        `questionnaire/user/${body.userId}/answer`,
        {
          id: body.id,
          answers: body.answers,
        }
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Answer questionnaire failed"
        );
      }
      return rejectWithValue("Answer questionnaire failed");
    }
  }
);

export const getDocuments = createAsyncThunk(
  "documents",
  async (_: GetDocumentsBody, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`documents`);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Get documents failed"
        );
      }
      return rejectWithValue("Get documents failed");
    }
  }
);

export const getMoreFiles = createAsyncThunk(
  "files/more",
  async (currentPage: number, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(
        `documents?page=${currentPage + 1}&type=files-only`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Get more files failed"
        );
      }
      return rejectWithValue("Get more files failed");
    }
  }
);

export const getMoreFilesForUser = createAsyncThunk(
  "files/more/user",
  async (
    { userId, currentPage }: GetMoreFilesForUserBody,
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.get(
        `documents/user/${userId}?page=${currentPage + 1}&type=files-only`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Get more files failed"
        );
      }
      return rejectWithValue("Get more files failed");
    }
  }
);

export const getMoreFolders = createAsyncThunk(
  "folders/more",
  async (currentPage: number, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(
        `documents?page=${currentPage + 1}&type=folders-only`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Get more folders failed"
        );
      }
      return rejectWithValue("Get more folders failed");
    }
  }
);

export const getMoreFoldersForUser = createAsyncThunk(
  "folders/more/user",
  async (
    { userId, currentPage }: GetMoreFoldersForUserBody,
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.get(
        `documents/user/${userId}?page=${currentPage + 1}&type=folders-only`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Get more folders failed"
        );
      }
      return rejectWithValue("Get more folders failed");
    }
  }
);

export const createDocument = createAsyncThunk(
  "document/create",
  async (body: CreateDocumentBody, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", body.file);
      if (body.folderId !== null) {
        formData.append("folderId", body.folderId);
      }
      const response = await apiClient.post(`document`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Create document failed"
        );
      }
      return rejectWithValue("Create document failed");
    }
  }
);

export const createFolder = createAsyncThunk(
  "documents/folder/create",
  async (body: CreateFolderBody, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`document/folder`, body);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Create folder failed"
        );
      }
      return rejectWithValue("Create folder failed");
    }
  }
);

export const createFolderForUser = createAsyncThunk(
  "documents/folder/create/user",
  async (body: CreateFolderForUserBody, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`document/user/${body.userId}/folder`, {
        name: body.name,
      });
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Create folder failed"
        );
      }
      return rejectWithValue("Create folder failed");
    }
  }
);

export const createDocumentForUser = createAsyncThunk(
  "document/create/user",
  async (body: CreateDocumentForUserBody, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", body.file);
      if (body.folderId !== null) {
        formData.append("folderId", body.folderId);
      }
      const response = await apiClient.post(
        `document/user/${body.userId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Create document failed"
        );
      }
      return rejectWithValue("Create document failed");
    }
  }
);

export const getDocumentsByFolder = createAsyncThunk(
  "documents/folder",
  async (
    { folderId, userId }: GetDocumentsByFolderBody,
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.get(
        `documents${userId ? `/user/${userId}` : ""}/folder/${folderId}`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Get documents failed"
        );
      }
      return rejectWithValue("Get documents failed");
    }
  }
);

export const getDocumentsByFolderForUser = createAsyncThunk(
  "documents/folder/user",
  async (
    { folderId, userId }: GetDocumentsByFolderForUserBody,
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.get(
        `documents/user/${userId}/folder/${folderId}`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Get documents failed"
        );
      }
      return rejectWithValue("Get documents failed");
    }
  }
);

export const getMoreFilesByFolder = createAsyncThunk(
  "files/more/folder",
  async (
    { folderId, currentPage }: GetMoreFilesByFolderBody,
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.get(
        `documents/folder/${folderId}?page=${currentPage + 1}&type=files-only`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Get more files failed"
        );
      }
      return rejectWithValue("Get more files failed");
    }
  }
);

export const getMoreFilesByFolderForUser = createAsyncThunk(
  "files/more/folder/user",
  async (
    { folderId, currentPage, userId }: GetMoreFilesByFolderForUserBody,
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.get(
        `documents/user/${userId}/folder/${folderId}?page=${
          currentPage + 1
        }&type=files-only`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Get more files failed"
        );
      }
      return rejectWithValue("Get more files failed");
    }
  }
);

export const getDocumentsForUser = createAsyncThunk(
  "documents/user",
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`documents/user/${userId}`);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Get documents failed"
        );
      }
      return rejectWithValue("Get documents failed");
    }
  }
);

export const deleteFolder = createAsyncThunk(
  `document/folder`,
  async ({ id }: { id: string }, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(`document/folder/${id}`);

      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Remove folder failed"
        );
      }
      return rejectWithValue("Remove folder failed");
    }
  }
);

export const deleteDocument = createAsyncThunk(
  "document/delete",
  async ({ id }: DeleteDocumentBody, { rejectWithValue }) => {
    try {
      const deleteResponse = await apiClient.delete(`document/${id}`);
      const responseData = deleteResponse.data.data;

      return responseData;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Remove document failed"
        );
      }
      return rejectWithValue("Remove document failed");
    }
  }
);

export const getNotifications = createAsyncThunk(
  "notifications",
  async ({ currentPage }: GetMoreNotifications, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(
        `notifications?page=${currentPage + 1}`
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message || "Get notifications failed"
        );
      }
      return rejectWithValue("Get notifications failed");
    }
  }
);

export const markAllNotificationsRead = createAsyncThunk(
  "notifications/mark-all-as-read",
  async (_: undefined, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`notifications/mark-all-as-read`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Mark all notifications read failed"
        );
      }
      return rejectWithValue("Mark all notifications read failed");
    }
  }
);

export const markNotificationRead = createAsyncThunk(
  "notifications/mark-as-read",
  async ({ id }: MarkNotificationRead, { rejectWithValue }) => {
    try {
      await apiClient.post(`notifications/mark-as-read?id=${id}`);
      return id;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.error?.message ||
            "Mark notification read failed"
        );
      }
      return rejectWithValue("Mark notification read failed");
    }
  }
);
